/**
 * Tour steps aggregator
 *
 * Uso:
 *   import { getTourSteps } from '../tours';
 *   const steps = getTourSteps({ mode: 'prospect', length: 'quick' });
 *
 * Parámetros:
 *   mode   — 'prospect' | 'onboarding'  (default: 'onboarding')
 *   length — 'quick'    | 'full'        (default: 'quick')
 *
 * Schema de cada paso:
 * {
 *   id: string,                                        // identificador único
 *   title: string,                                     // título del tooltip
 *   description: string,                               // cuerpo del tooltip
 *   icon: React.ComponentType,                         // Phosphor icon component
 *   position: 'center'|'bottom'|'bottom-right'|       // posición del tooltip
 *             'bottom-left'|'right'|'left'|'top',
 *   highlight: string | null,                          // CSS selector elemento a resaltar
 *   fallbackSelector: string | null,                   // selector alternativo si highlight no existe
 *   mode: 'prospect' | 'onboarding' | 'both',         // NUEVO — para qué modo aplica
 *   length: 'quick' | 'full' | 'both',                // NUEVO — en qué granularidad aparece
 *   domain: 'dashboard'|'agents'|'attacks'|           // NUEVO — dominio funcional
 *           'detection'|'incidents'|'compliance'|
 *           'integrations'|'reports',
 *   videoFallback: null | { src: string, poster: string } // NUEVO — placeholder para vídeo inline
 * }
 */

import { domainSteps as dashboardSteps } from './dashboard';
import { domainSteps as agentsSteps } from './agents';
import { domainSteps as attacksSteps } from './attacks';
import { domainSteps as detectionSteps } from './detection';
import { domainSteps as incidentsSteps } from './incidents';
import { domainSteps as complianceSteps } from './compliance';
import { domainSteps as integrationsSteps } from './integrations';
import { domainSteps as reportsSteps } from './reports';

/** Orden canónico de los dominios en el tour */
const DOMAIN_ORDER = [
  dashboardSteps,
  agentsSteps,
  complianceSteps,       // vulnerabilities — posición original paso 4
  integrationsSteps,     // network — posición original paso 5
  attacksSteps,
  reportsSteps,          // reports + notifications + search
  incidentsSteps,
  detectionSteps,
];

/**
 * Devuelve el array de pasos para el modo y granularidad solicitados.
 * El último paso (complete) siempre viene de dashboard y se añade al final.
 *
 * @param {Object} options
 * @param {'prospect'|'onboarding'} [options.mode='onboarding']
 * @param {'quick'|'full'} [options.length='quick']
 * @returns {Array}
 */
export function getTourSteps({ mode = 'onboarding', length = 'quick' } = {}) {
  const safeMode = mode === 'prospect' ? 'prospect' : 'onboarding';
  const safeLength = length === 'full' ? 'full' : 'quick';

  const steps = [];

  for (const domain of DOMAIN_ORDER) {
    const domainSlice = domain?.[safeMode]?.[safeLength] ?? [];
    steps.push(...domainSlice);
  }

  // Deduplicar por id (por si dashboard.js ya incluyó welcome/complete y otro dominio también los tiene)
  const seen = new Set();
  return steps.filter(step => {
    if (seen.has(step.id)) return false;
    seen.add(step.id);
    return true;
  });
}
